import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { getAllUsersData } from "selectors/organizations";
import { getTextsData } from "selectors/texts";
import { useSelector } from "react-redux";
import ActivityCell from "./lib/ActivityCell";
import Arrays from "utils/Arrays";
import DataStatuses from "const/DataStatuses";
import IconCell from "./lib/IconCell";
import LocalStorageKeys from "const/LocalStorageKeys";
import NameCell from "./lib/NameCell";
import Objects from "utils/Objects";
import React, { useCallback, useMemo } from "react";
import Storage from "utils/Storage";
import TableNew, { TableHead, TableRow } from "lib/common/TableNew";
import UiRoutes from "const/UiRoutes";
import Urls from "utils/Urls";
import UserRoles from "const/UserRoles";
import classNames from "classnames";
import useEnvVars from "hooks/useEnvVars";

const { TASKS_VIEW_LAST_TAB } = LocalStorageKeys;

const SummaryTable = ({ data }) => {
  const [{ sortBy, sortOrder }, setEnvVars] = useEnvVars();

  const { uiTexts } = useSelector(getTextsData);

  const usersData = useSelector(getAllUsersData);

  const tasksViewLastTab = Storage.storageValue(TASKS_VIEW_LAST_TAB) || "list";

  const hasBusinessUsers = data.some(({ business: { id: businessId } }) => {
    return usersData.some(({ role, businessIds }) => {
      return UserRoles.checkIsBusiness(role) && businessIds.includes(businessId);
    });
  });

  const sorted = useMemo(() => {
    switch (sortBy) {
      case "tasks.forBusinesses":
      case "tasks.forAccountants":
      case "transactions.exported":
        return Arrays.sort(data, (item) => {
          const { current = 0, total = 0 } = Objects.getPropertyByPath(item, sortBy) || {};

          return total ? current / total : -1;
        }, sortOrder === "asc");
      case "lastActivityEvent":
        return Arrays.sort(data, (item) => {
          return Objects.getPropertyByPath(item, "lastActivityEvent.timestamp") || "";
        }, sortOrder !== "asc");
      default:
        return Arrays.sort(data, (item) => {
          return Objects.getPropertyByPath(item, sortBy);
        }, sortOrder === "asc");
    }
  }, [data, sortBy, sortOrder]);

  const handleSortChange = useCallback((sortParams) => {
    setEnvVars(sortParams);
  }, [setEnvVars]);

  return (
    <TableNew
      theme="flat"
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortChange={handleSortChange}
      className={Css.table}>
      <TableRow className={Css.row}>
        <TableHead
          className={classNames(Css.head, Css.nameCell)}
          accessor="business.name">
          {uiTexts.business}
        </TableHead>
        <TableHead
          title={uiTexts.documentsToReview}
          className={classNames(Css.head, Css.iconCell)}
          accessor="documents.toReview.current">
          {uiTexts.documentsToReview}
        </TableHead>
        <TableHead
          title={uiTexts.tasksForAccountant}
          className={classNames(Css.head, Css.iconCell)}
          accessor="tasks.forAccountants"
          show={hasBusinessUsers}>
          {uiTexts.tasksForAccountant}
        </TableHead>
        <TableHead
          title={uiTexts.tasksForClient}
          className={classNames(Css.head, Css.iconCell)}
          accessor="tasks.forBusinesses"
          show={hasBusinessUsers}>
          {uiTexts.tasksForClient}
        </TableHead>
        <TableHead
          title={uiTexts.recentActivity}
          accessor="lastActivityEvent"
          className={classNames(Css.head, Css.activityCell)}>
          {uiTexts.recentActivity}
        </TableHead>
      </TableRow>
      {sorted.map((item) => {
        const { business: { id: businessId, name, extraData = {}, settings }, lastActivityEvent } = item;

        const { DOCUMENTS, TASKS } = UiRoutes;

        const currentBusinessHasBusinessUsers = usersData.some(({ role, businessIds }) => {
          return UserRoles.checkIsBusiness(role) && businessIds.includes(businessId);
        });

        return (
          <TableRow key={businessId} className={Css.row}>
            <NameCell
              className={classNames(Css.nameCell)}
              name={name}
              businessId={businessId}
              extraData={extraData}
              settings={settings} />
            <IconCell
              className={Css.iconCell}
              theme="primary"
              iconComponent={Icons.FileText}
              value={Objects.getPropertyByPath(item, "documents.toReview.current")}
              href={`/${businessId}${DOCUMENTS}?${Urls.objectToQueryString({ status: DataStatuses.TO_REVIEW })}`} />
            <IconCell
              progress
              show={hasBusinessUsers}
              emptyState={!currentBusinessHasBusinessUsers}
              className={Css.iconCell}
              theme="success"
              iconComponent={Icons.ChatCircleText}
              value={Objects.getPropertyByPath(item, "tasks.forAccountants")}
              href={`/${businessId}${TASKS}/${tasksViewLastTab}?targetUser=accountant`} />
            <IconCell
              progress
              show={hasBusinessUsers}
              emptyState={!currentBusinessHasBusinessUsers}
              className={Css.iconCell}
              theme="accent"
              iconComponent={Icons.Lightning}
              value={Objects.getPropertyByPath(item, "tasks.forBusinesses")}
              href={`/${businessId}${TASKS}/${tasksViewLastTab}?targetUser=client`} />
            <ActivityCell
              businessId={businessId}
              lastActivityEvent={lastActivityEvent}
              className={Css.activityCell} />
          </TableRow>
        );
      })}
    </TableNew>
  );
};

export default React.memo(SummaryTable);
